import React, { useEffect, useState } from "react"
import { resolveMediaUrl } from "../../helpers/userAvatar"
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("")

/**
 * Avatar
 * ------------------------------------------------------------------
 * Single circular avatar. Renders an <img> when `src` is given,
 * otherwise falls back to initials on a muted background so missing
 * photos never break the layout.
 *
 * @param {string} src
 * @param {string} name - used for alt text and the initials fallback
 * @param {'xs'|'sm'|'md'|'lg'} size
 */
export const Avatar = ({
  src,
  name = "",
  size = "sm",
  className = "",
  inactive = false,
}) => {
  const [failed, setFailed] = useState(false)
  const resolvedSrc = resolveMediaUrl(src)

  useEffect(() => {
    setFailed(false)
  }, [resolvedSrc])

  const label = name || "Collaborator"
  const showImage = Boolean(resolvedSrc) && !failed

  return (
    <span
      className={`avatar avatar--${size} ${className} ${inactive ? "avatar--inactive" : ""}`}
      title={label}
      aria-label={label}
    >
      {showImage ? (
        <img
          key={resolvedSrc}
          className="avatar__img"
          src={resolvedSrc}
          alt={label}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="avatar__fallback" aria-hidden="true">
          {initials(label)}
        </span>
      )}
    </span>
  )
}

/**
 * AvatarGroup
 * ------------------------------------------------------------------
 * Overlapping stack of avatars with a "+N" overflow chip once the
 * list is longer than `max`. Used for project collaborators and
 * session participants.
 *
 * @param {{src?: string, name: string}[]} people
 * @param {number} max - how many circles to show before collapsing into "+N"
 * @param {'xs'|'sm'|'md'|'lg'} size
 */
export const AvatarGroup = ({
  people = [],
  max = 3,
  size = "sm",
  total,
  className = "",
}) => {
  const visible = people.slice(0, max)
  const baseTotal = typeof total === "number" ? total : people.length
  const overflow = Math.max(0, baseTotal - visible.length)

  if (visible.length === 0) return null

  return (
    <span className={`avatar-group ${className}`}>
      {visible.map((person, i) => (
        <Avatar
          key={i}
          src={person.src || person.avatar}
          name={person.name}
          size={size}
          className="avatar-group__item"
          inactive={person.inactive}
        />
      ))}
      {overflow > 0 && (
        <span
          className={`avatar avatar--${size} avatar-group__item avatar-group__overflow`}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}

export default Avatar
